import { useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import { SocialProofImage } from '../../redux/states/firebaseRemoteConfig/firebaseRemoteConfig.types'
import { RootState } from '../../redux/store/store.types'
import { SocialProofItem } from '../components/SocialProofBanner/SocialProofBanner.types'
import firebase from '../functions/Firebase/firebase.functions'
import logger from '../functions/Logger/logger.functions'

interface UseSocialProofItemsReturn {
  items: SocialProofItem[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const useSocialProofItems = (fallbackItems: SocialProofItem[] = []): UseSocialProofItemsReturn => {
  const [items, setItems] = useState<SocialProofItem[]>(fallbackItems)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Get socialProofImages from Firebase Remote Config via Redux
  const firebaseRemoteConfigValues = useSelector((state: RootState) => state.firebaseRemoteConfig.values)
  const remoteConfigSocialProofImages = firebaseRemoteConfigValues.socialProofImages as SocialProofImage[] | undefined

  const fetchSocialProofItems = async (): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      // First try to get from Firebase Remote Config
      if (firebase.remoteConfig) {
        await firebase.remoteConfig.refreshConfig()
        const remoteImages = firebase.remoteConfig.getConfigValue<SocialProofImage[]>('socialProofImages')

        if (remoteImages && remoteImages.length > 0) {
          // Convert SocialProofImage to SocialProofItem format
          const convertedItems: SocialProofItem[] = remoteImages.map((image) => ({
            imageUrl: image.imageUrl,
            linkUrl: image.linkUrl,
            altText: image.altText
          }))
          setItems(convertedItems)

          return
        }
      }

      // Fallback to Redux state if Remote Config fails
      if (remoteConfigSocialProofImages && remoteConfigSocialProofImages.length > 0) {
        const convertedItems: SocialProofItem[] = remoteConfigSocialProofImages.map((image) => ({
          imageUrl: image.imageUrl,
          linkUrl: image.linkUrl,
          altText: image.altText
        }))
        setItems(convertedItems)

        return
      }

      // Final fallback to provided fallback items
      if (fallbackItems.length > 0) {
        setItems(fallbackItems)
      }
    } catch (error) {
      logger.logError(error, 'Error fetching social proof items from Remote Config', 'useSocialProofItems')
      setError('Failed to load social proof items')

      // Use fallback items if Firebase fails
      if (fallbackItems.length > 0) {
        setItems(fallbackItems)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Try Remote Config first, then fallback items
    if (remoteConfigSocialProofImages && remoteConfigSocialProofImages.length > 0) {
      const convertedItems: SocialProofItem[] = remoteConfigSocialProofImages.map((image) => ({
        imageUrl: image.imageUrl,
        linkUrl: image.linkUrl,
        altText: image.altText
      }))
      setItems(convertedItems)
    } else if (fallbackItems.length > 0) {
      setItems(fallbackItems)
    } else {
      fetchSocialProofItems()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteConfigSocialProofImages, fallbackItems.length])

  return {
    items,
    loading,
    error,
    refetch: fetchSocialProofItems
  }
}
